/**
 * Product page carousels: slides past the first visible page are rendered as placeholders
 * (li.pdp-lazy-slide with data-handle) and filled with real cards on the first interaction with
 * their section (pointer over, focus, touch or slider scroll). Cards come from
 * /products/<handle>?section_id=pdp-lazy-card-<size>, so these requests are cacheable per product.
 *
 * Markup contract:
 *   [data-pdp-lazy-scope]                  section wrapper the interaction is detected on
 *     ul[data-pdp-lazy-list]               slide list
 *       data-pdp-lazy-visible="4"          slides that must always be real (first page)
 *       data-pdp-lazy-size="medium"        card size of the endpoint section
 *       data-pdp-lazy-highlight="false"    strip the highlight style like the section does
 *       li.pdp-lazy-slide[data-handle]     placeholder
 *
 * Listeners are delegated on document, so sections inserted later (recently viewed) and
 * carousels that get cloned (same-vendor / mixed-vendor reinit) need no extra binding.
 */
(() => {
  if (window.PdpLazySlides) return;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const requests = {};

  function fetchCard(handle, size) {
    const key = `${size}:${handle}`;
    if (!requests[key]) {
      requests[key] = fetch(`${root}products/${encodeURIComponent(handle)}?section_id=pdp-lazy-card-${size}`)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const tpl = document.createElement('template');
          tpl.innerHTML = html;
          const wrapper = tpl.content.querySelector('.shopify-section') || tpl.content;
          if (!wrapper.querySelector('product-card')) throw new Error('empty');
          return wrapper.innerHTML;
        });
      requests[key].catch(() => { delete requests[key]; });
    }
    return requests[key];
  }

  // Resolves to true when the slide had to be removed.
  function fillSlide(li, list) {
    li.pdpLazyLoading = true;

    return fetchCard(li.dataset.handle, list.dataset.pdpLazySize || 'medium').then((markup) => {
      if (!li.isConnected) return false;
      li.innerHTML = markup;
      if (list.dataset.pdpLazyHighlight === 'false') {
        li.querySelectorAll('.card--highlight-product').forEach((card) => card.classList.remove('card--highlight-product'));
      }
      li.classList.remove('pdp-lazy-slide');
      li.removeAttribute('aria-hidden');
      return false;
    }, () => {
      if (!li.isConnected) return false;
      li.remove();
      return true;
    });
  }

  function settle(list, removed) {
    document.dispatchEvent(new CustomEvent('badge-manager:rules-ready'));
    if (!removed) return;

    // The carousel keeps the slide list it read on init; drop the removed slides from it.
    const carousel = list.closest('carousel-slider');
    if (carousel && Array.isArray(carousel.slides)) {
      carousel.slides = carousel.slides.filter((slide) => slide.isConnected);
    }
    window.dispatchEvent(new Event('resize'));
  }

  function load(scope, onlyVisible) {
    scope.querySelectorAll('[data-pdp-lazy-list]').forEach((list) => {
      // Desktop carousels are display:none on mobile when the section uses a mobile grid
      if (!list.getClientRects().length) return;
      const visible = parseInt(list.dataset.pdpLazyVisible, 10) || 0;
      const jobs = [];

      Array.from(list.children).forEach((li, index) => {
        if (!li.classList.contains('pdp-lazy-slide') || !li.dataset.handle || li.pdpLazyLoading) return;
        if (onlyVisible && index >= visible) return;
        jobs.push(fillSlide(li, list));
      });

      if (jobs.length) {
        Promise.all(jobs).then((results) => settle(list, results.includes(true)));
      }
    });
  }

  function activate(scope) {
    scope.pdpLazyActive = true;
    load(scope, false);
  }

  // For section scripts that add, remove or clone slides: fills placeholders that moved into the
  // first page, or all of them once the section has been interacted with.
  function wake(scope) {
    if (scope) load(scope, !scope.pdpLazyActive);
  }

  function onInteract(event) {
    const target = event.target;
    if (!target || !target.closest) return;
    // OverlayScrollbars' size observers fire scroll events on their own; only a moved slider counts
    if (event.type === 'scroll' && !(target.classList.contains('slider') && target.scrollLeft > 0)) return;
    // Placeholders in a hidden list stay pending, so a later interaction after a resize still fills them
    const scope = target.closest('[data-pdp-lazy-scope]');
    if (scope && scope.querySelector('.pdp-lazy-slide')) activate(scope);
  }

  document.addEventListener('pointerover', onInteract, { passive: true });
  document.addEventListener('focusin', onInteract);
  document.addEventListener('touchstart', onInteract, { passive: true });
  document.addEventListener('scroll', onInteract, { capture: true, passive: true });

  window.PdpLazySlides = { activate, wake };
})();
